import {imageIdentity, VariantImageCache} from './growthlab-image-loader.js';

export function resolveZeus(variants, options, quantity) {
  const variant = variants.find(item => item.options.every((value, index) => value === options[index]));
  const error = !variant?.available ? 'unavailable' : (!Number.isSafeInteger(quantity) || quantity < 1 ? 'quantity_error' : null);
  return {variant, error, total: error ? null : variant.price * quantity, items: error ? [] : [{id:variant.id, quantity}]};
}

// Extra cost of one option value over the cheapest value of that option, other choices unchanged.
function zeusPriceDifference(variants, options, index, value) {
  const siblings = variants.filter(item => item.options.every((choice, position) => position === index || choice === options[position]));
  const match = siblings.find(item => item.options[index] === value);
  return match ? match.price - Math.min(...siblings.map(item => item.price)) : null;
}

class GrowthlabZeus extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.listeners = new AbortController();
    const listenerOptions = {signal:this.listeners.signal};
    this.catalog = JSON.parse(this.querySelector('[data-zeus-data]').textContent);
    this.form = this.querySelector('form');
    this.inputs = [...this.querySelectorAll('[data-option-index]')];
    this.quantity = this.querySelector('[name="quantity"]');
    this.image = this.querySelector('[data-zeus-image]');
    this.imageRequest = (this.imageRequest || 0) + 1;
    this.galleryCache = new VariantImageCache();
    this.money = new Intl.NumberFormat(this.dataset.locale, {style:'currency', currency:this.dataset.currency, currencyDisplay:'narrowSymbol'});
    this.querySelector('[data-fallback]').hidden = true;
    this.querySelector('[data-fallback] select').disabled = true;
    this.querySelector('[data-variant-id]').disabled = false;
    this.querySelector('.gz-enhanced').hidden = false;
    this.addEventListener('change', event => {
      if (event.target.matches('[data-option-index]')) this.update(true);
      if (event.target === this.quantity) this.update();
    }, listenerOptions);
    this.quantity.addEventListener('input', () => this.update(), listenerOptions);
    const warmChoice = event => {
      const input = event.target.closest('label')?.querySelector('[data-option-index]');
      if (!input || input.checked || input.disabled || !this.state?.variant) return;
      const options = [...this.state.variant.options];
      options[Number(input.dataset.optionIndex)] = input.value;
      const variant = this.catalog.variants.find(item => item.available && item.options.every((value, index) => value === options[index]));
      if (variant) this.galleryCache.warm(variant.image);
    };
    this.addEventListener('pointerover', warmChoice, listenerOptions);
    this.addEventListener('focusin', warmChoice, listenerOptions);
    this.addEventListener('click', event => {
      const step = event.target.closest('[data-quantity-step]');
      if (step && !this.busy) {
        const current = Number(this.quantity.value);
        this.quantity.value = Math.max(1, (Number.isSafeInteger(current) ? current : 1) + Number(step.dataset.quantityStep));
        this.update();
      }
      const photo = event.target.closest('[data-photo]');
      if (photo) this.showImage(photo.dataset.photo, photo.getAttribute('aria-label'));
    }, listenerOptions);
    this.form.addEventListener('submit', event => this.submit(event), listenerOptions);
    this.onPopstate = () => {
      const variant = this.catalog.variants.find(item => String(item.id) === new URL(location.href).searchParams.get('variant'));
      if (!variant) return;
      this.inputs.forEach(input => input.checked = variant.options[Number(input.dataset.optionIndex)] === input.value);
      this.update(true, false);
    };
    window.addEventListener('popstate', this.onPopstate, listenerOptions);
    this.update(true, false);
  }
  disconnectedCallback() {
    this.listeners?.abort();
    this.galleryCache?.cancel();
    this.imageRequest++;
    this.image?.parentElement.removeAttribute('aria-busy');
    this.ready = false;
  }
  update(changeImage = false, updateURL = true) {
    const options = [];
    this.inputs.filter(input => input.checked).forEach(input => options[Number(input.dataset.optionIndex)] = input.value);
    this.state = resolveZeus(this.catalog.variants, options, Number(this.quantity.value));
    const {variant, error, total} = this.state;
    this.querySelector('[data-total]').textContent = this.money.format((total ?? variant?.price ?? 0) / 100);
    this.querySelectorAll('[data-material-price]').forEach(note => {
      const input = note.closest('label').querySelector('[data-option-index]');
      const extra = zeusPriceDifference(this.catalog.variants, options, Number(input.dataset.optionIndex), input.value);
      note.textContent = extra === null ? '' : (extra === 0 ? this.catalog.labels.base_finish : '+ ' + this.money.format(extra / 100));
    });
    this.querySelector('[data-submit]').disabled = !!error || !!this.busy;
    this.querySelector('[data-submit]').textContent = this.busy ? this.catalog.labels.adding : this.catalog.labels.add;
    this.querySelector('[data-variant-id]').value = variant?.id || '';
    this.querySelector('[data-variant-id]').disabled = !!error;
    this.querySelector('[data-cart-error]').textContent = error ? this.catalog.labels[error] : (this.cartError || '');
    this.inputs.forEach(input => {
      const candidate = [...options];
      candidate[Number(input.dataset.optionIndex)] = input.value;
      input.disabled = !!this.busy || !this.catalog.variants.some(item => item.available && item.options.every((value, index) => value === candidate[index]));
    });
    this.quantity.disabled = !!this.busy;
    if (variant && updateURL) {
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }
    if (variant && changeImage) this.showImage(variant.image, this.catalog.labels.photo);
    else if (changeImage) {
      this.galleryCache.cancel();
      this.imageRequest++;
      this.image.parentElement.removeAttribute('aria-busy');
    }
  }
  async showImage(src, alt) {
    if (!this.image || !src) return;
    this.galleryCache.cancel();
    const request = ++this.imageRequest;
    const selectedImage = imageIdentity(src);
    const markThumbnail = () => this.querySelectorAll('[data-photo]').forEach(button => button.setAttribute('aria-pressed', String(imageIdentity(button.dataset.photo) === selectedImage)));
    const preload = () => {
      const variant = this.state?.variant;
      this.galleryCache.schedule(this.catalog.variants, variant, () => this.isConnected && request === this.imageRequest && this.state?.variant?.id === variant?.id);
    };
    if (selectedImage === imageIdentity(this.image.currentSrc || this.image.getAttribute('src'))) {
      this.image.parentElement.removeAttribute('aria-busy');
      markThumbnail();
      if (this.image.complete && this.image.naturalWidth) preload();
      else this.image.addEventListener('load', () => { if (request === this.imageRequest && this.isConnected) preload(); }, {once:true, signal:this.listeners.signal});
      return;
    }
    this.image.parentElement.setAttribute('aria-busy', 'true');
    try {
      const loaded = await this.galleryCache.load(src);
      if (request !== this.imageRequest || !this.isConnected) return;
      this.image.src = loaded;
      this.image.removeAttribute('srcset');
      this.image.alt = alt;
      markThumbnail();
      preload();
    } catch {} finally {
      if (request === this.imageRequest) this.image.parentElement.removeAttribute('aria-busy');
    }
  }
  async submit(event) {
    event.preventDefault();
    if (this.busy) return;
    this.cartError = '';
    this.update();
    if (this.state.error || !this.form.reportValidity()) return;
    const items = this.state.items.map(item => ({...item}));
    this.busy = true;
    this.querySelector('[data-cart-fallback]').hidden = true;
    this.update();
    try {
      const {addToDrawer} = await import(this.dataset.cartModule);
      await addToDrawer(items, this.dataset.addUrl, this.dataset.cartUrl);
    } catch (error) {
      this.cartError = error.confirmed ? this.catalog.labels.cart_added : (error.rejected ? error.message : this.catalog.labels.cart_uncertain);
      this.querySelector('[data-cart-fallback]').hidden = false;
    } finally {
      this.busy = false;
      this.update();
    }
  }
}
if (!customElements.get('growthlab-zeus')) customElements.define('growthlab-zeus', GrowthlabZeus);
